import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import {
  buildEventDetailsMockFromState,
  formatBRL,
  getBannerSrc,
} from "../../utils/normalizers";

const sectionTitleSx = {
  fontWeight: 900,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.94)",
  mb: 0.45,
};

const sectionSubSx = {
  fontSize: "0.84rem",
  color: "rgba(255,255,255,0.5)",
  mb: 2,
  lineHeight: 1.6,
};

const cardSx = {
  p: 2,
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.08)",
  bgcolor: "rgba(255,255,255,0.03)",
  height: "100%",
};

const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.4} alignItems="flex-start">
    <Avatar
      variant="rounded"
      sx={{
        width: 34,
        height: 34,
        bgcolor: "rgba(0,225,255,0.12)",
        color: "#8ef4ff",
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Avatar>
    <Box>
      <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,0.45)", lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, color: "rgba(255,255,255,0.92)", mt: 0.45 }}>
        {value || "-"}
      </Typography>
    </Box>
  </Stack>
);

export default function RevisaoStep({ state }) {
  const mock = useMemo(() => buildEventDetailsMockFromState(state), [state]);
  const banner = getBannerSrc(state);

  const totalTickets = state.ingressos.reduce((acc, t) => acc + Number(t.quantidade || 0), 0);
  const menorPreco = state.ingressos.length
    ? Math.min(...state.ingressos.map((t) => Number(t.preco || 0)))
    : 0;

  return (
    <Box>
      <Typography sx={sectionTitleSx}>Revisão final</Typography>
      <Typography sx={sectionSubSx}>
        Confira tudo com calma antes de publicar. O objetivo aqui é enxergar o evento como o cliente vai perceber.
      </Typography>

      <Grid container spacing={1.8}>
        <Grid item xs={12}>
          <Paper sx={{ ...cardSx, overflow: "hidden", p: 0 }}>
            <Box
              sx={{
                minHeight: { xs: 180, md: 220 },
                backgroundImage: banner
                  ? `linear-gradient(180deg, rgba(5,12,24,0.12), rgba(5,12,24,0.65)), url(${banner})`
                  : "linear-gradient(135deg, rgba(0,225,255,0.15), rgba(109,255,178,0.1))",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <Box sx={{ p: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.5, mb: 0.75 }}>
                    {state.identidade.titulo || "Evento sem nome"}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={state.identidade.categoria || "Geral"} sx={{ fontWeight: 800 }} />
                    <Chip size="small" label={`Classificação: ${state.identidade.classificacao || "?"}`} sx={{ fontWeight: 800 }} />
                    <Chip size="small" label={`${state.identidade.tags?.length || 0} tag(s)`} sx={{ fontWeight: 800 }} />
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Chip
                    icon={<AttachMoneyRoundedIcon sx={{ fontSize: 16 }} />}
                    label={`A partir de ${formatBRL(menorPreco)}`}
                    sx={{ fontWeight: 900 }}
                  />
                  <Chip
                    icon={<ConfirmationNumberRoundedIcon sx={{ fontSize: 16 }} />}
                    label={`${totalTickets} ingressos`}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardSx}>
            <Typography sx={{ fontWeight: 900, mb: 1.6 }}>Quando e onde</Typography>
            <Stack spacing={2}>
              <InfoRow icon={<CalendarMonthRoundedIcon />} label="Data" value={mock.date} />
              <InfoRow
                icon={<AccessTimeRoundedIcon />}
                label="Horário"
                value={`${state.dataLocal.horaInicio || "?"}${state.dataLocal.horaFim ? ` • até ${state.dataLocal.horaFim}` : ""}`}
              />
              <InfoRow
                icon={<PlaceRoundedIcon />}
                label="Local"
                value={`${state.dataLocal.local.nome || "Local não informado"}${state.dataLocal.local.cidade ? ` • ${state.dataLocal.local.cidade}/${state.dataLocal.local.estado}` : ""}`}
              />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={cardSx}>
            <Typography sx={{ fontWeight: 900, mb: 1.6 }}>Bilheteria</Typography>
            <Stack spacing={1}>
              {state.ingressos.length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.46)" }}>
                  Nenhum ingresso configurado.
                </Typography>
              ) : (
                state.ingressos.map((t, idx) => (
                  <Stack
                    key={t.id || idx}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,0.08)",
                      bgcolor: "rgba(255,255,255,0.03)",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>
                        {t.nome || `Ingresso ${idx + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.48)" }}>
                        {t.lote || "Lote"} • {t.quantidade || 0} un.
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 900, color: "#9dffc8" }}>
                      {formatBRL(t.preco)}
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={cardSx}>
            <Typography sx={{ fontWeight: 900, mb: 1.2 }}>Descrição resumida</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7, whiteSpace: "pre-line" }}>
              {state.descricao.texto?.trim() || "Nenhuma descrição preenchida ainda."}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={cardSx}>
            <Typography sx={{ fontWeight: 900, mb: 1.6 }}>Regras e operação</Typography>
            <Stack spacing={2}>
              <InfoRow icon={<ShieldRoundedIcon />} label="Menores" value={state.regras.permiteMenores ? "Permitido com regras" : "Não permitido"} />
              <InfoRow icon={<EventRoundedIcon />} label="Limite por usuário" value={`${state.vendas.limitePorUsuario || 0} ingresso(s)`} />
              <InfoRow icon={<AttachMoneyRoundedIcon />} label="Taxa de serviço" value={`${state.vendas.taxaOnurbPercent || 0}%`} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
